import type { ReactNode } from 'react';

/**
 * The glyphs, drawn exactly as the component gallery's `GLYPH` table and the
 * prototype draw them: a 16-unit box, a 1.25 stroke, square caps, no fill.
 * « » ⇄ ← × stay characters, as they are in the prototype. An icon never
 * stands alone as a control's name — every IconButton carries a label.
 */
export type Glyph =
  | 'add'
  | 'findTime'
  | 'calendar'
  | 'approvals'
  | 'people'
  | 'agents'
  | 'reports'
  | 'safety'
  | 'ask'
  | 'records'
  | 'settings'
  | 'check'
  | 'warning'
  | 'stop'
  | 'bell'
  | 'search'
  | 'map'
  | 'page'
  | 'mail'
  | 'folder'
  | 'register'
  | 'model'
  | 'help'
  | 'suggest'
  | 'close'
  | 'copy'
  | 'open'
  | 'edit'
  | 'back'
  | 'forward'
  | 'trash'
  | 'pause'
  | 'play'
  | 'pin'
  | 'hide'
  | 'checkAll'
  | 'up'
  | 'down'
  | 'undo'
  | 'menu'
  | 'chevron'
  | 'clock'
  | 'swap'
  | 'collapse';

const PATHS: Record<Glyph, ReactNode> = {
  add: (
    <>
      <line x1="8" y1="3" x2="8" y2="13" />
      <line x1="3" y1="8" x2="13" y2="8" />
    </>
  ),
  findTime: (
    <>
      <circle cx="8" cy="8" r="5.5" />
      <polyline points="8,4.5 8,8 10.5,9.5" />
    </>
  ),
  calendar: (
    <>
      <rect x="2.5" y="3.5" width="11" height="10" />
      <line x1="2.5" y1="6.5" x2="13.5" y2="6.5" />
      <line x1="5.5" y1="2" x2="5.5" y2="4" />
      <line x1="10.5" y1="2" x2="10.5" y2="4" />
    </>
  ),
  approvals: (
    <>
      <rect x="2.5" y="2.5" width="11" height="11" />
      <line x1="5" y1="8.5" x2="7" y2="10.5" />
      <line x1="7" y1="10.5" x2="11" y2="6" />
    </>
  ),
  people: (
    <>
      <circle cx="8" cy="5.5" r="2.5" />
      <line x1="3" y1="13.5" x2="13" y2="13.5" />
      <line x1="3" y1="13.5" x2="3" y2="11" />
      <line x1="13" y1="13.5" x2="13" y2="11" />
    </>
  ),
  agents: (
    <>
      <circle cx="8" cy="5.5" r="2.5" />
      <line x1="3" y1="13.5" x2="13" y2="13.5" />
      <rect x="10.5" y="10" width="3.5" height="3.5" fill="currentColor" stroke="none" />
    </>
  ),
  reports: (
    <>
      <line x1="3" y1="13" x2="3" y2="8" />
      <line x1="8" y1="13" x2="8" y2="3" />
      <line x1="13" y1="13" x2="13" y2="6" />
    </>
  ),
  safety: (
    <>
      <rect x="2.5" y="2.5" width="11" height="11" />
      <line x1="8" y1="5" x2="8" y2="11" />
    </>
  ),
  ask: (
    <>
      <rect x="2.5" y="3" width="11" height="8" />
      <line x1="5" y1="11" x2="5" y2="13.5" />
      <line x1="5" y1="13.5" x2="8" y2="11" />
    </>
  ),
  records: (
    <>
      <rect x="2.5" y="2.5" width="11" height="4" />
      <rect x="2.5" y="9.5" width="11" height="4" />
    </>
  ),
  settings: (
    <>
      <circle cx="8" cy="8" r="2.5" />
      <line x1="8" y1="2" x2="8" y2="4" />
      <line x1="8" y1="12" x2="8" y2="14" />
      <line x1="2" y1="8" x2="4" y2="8" />
      <line x1="12" y1="8" x2="14" y2="8" />
    </>
  ),
  check: (
    <>
      <line x1="3" y1="8.5" x2="6.5" y2="12" />
      <line x1="6.5" y1="12" x2="13" y2="4.5" />
    </>
  ),
  warning: (
    <>
      <line x1="8" y1="2.5" x2="2.5" y2="13.5" />
      <line x1="8" y1="2.5" x2="13.5" y2="13.5" />
      <line x1="2.5" y1="13.5" x2="13.5" y2="13.5" />
      <line x1="8" y1="7" x2="8" y2="10" />
    </>
  ),
  stop: (
    <>
      <rect x="2.5" y="2.5" width="11" height="11" />
      <rect x="6" y="6" width="4" height="4" fill="currentColor" stroke="none" />
    </>
  ),
  bell: (
    <>
      <path d="M4 6.5a4 4 0 0 1 8 0c0 3 1 4 1 4H3s1-1 1-4Z" />
      <path d="M6.5 13a1.6 1.6 0 0 0 3 0" />
    </>
  ),
  search: (
    <>
      <circle cx="7" cy="7" r="4.5" />
      <line x1="10.5" y1="10.5" x2="14" y2="14" />
    </>
  ),
  // A pin: a place.
  map: (
    <>
      <path d="M8 14s-4.5-4.2-4.5-7.5a4.5 4.5 0 0 1 9 0C12.5 9.8 8 14 8 14Z" />
      <circle cx="8" cy="6.5" r="1.5" />
    </>
  ),
  // A page with lines: something read.
  page: (
    <>
      <path d="M3.5 2.5h6l3 3v8h-9Z" />
      <line x1="5.5" y1="8" x2="10.5" y2="8" />
      <line x1="5.5" y1="10.5" x2="10.5" y2="10.5" />
    </>
  ),
  mail: (
    <>
      <rect x="2.5" y="3.5" width="11" height="9" />
      <polyline points="2.5,4 8,8.5 13.5,4" />
    </>
  ),
  folder: (
    <>
      <path d="M2.5 4.5h4l1.5 1.5h5.5v7h-11Z" />
    </>
  ),
  // A building with columns: a public register.
  register: (
    <>
      <polyline points="2,6 8,2.5 14,6" />
      <line x1="4" y1="7" x2="4" y2="12" />
      <line x1="8" y1="7" x2="8" y2="12" />
      <line x1="12" y1="7" x2="12" y2="12" />
      <line x1="2" y1="13.5" x2="14" y2="13.5" />
    </>
  ),
  // A chip: a model.
  model: (
    <>
      <rect x="4.5" y="4.5" width="7" height="7" />
      <line x1="6.5" y1="2" x2="6.5" y2="4.5" />
      <line x1="9.5" y1="2" x2="9.5" y2="4.5" />
      <line x1="6.5" y1="11.5" x2="6.5" y2="14" />
      <line x1="9.5" y1="11.5" x2="9.5" y2="14" />
      <line x1="2" y1="8" x2="4.5" y2="8" />
      <line x1="11.5" y1="8" x2="14" y2="8" />
    </>
  ),
  help: (
    <>
      <circle cx="8" cy="8" r="6" />
      <path d="M6.2 6.3a1.9 1.9 0 1 1 2.6 1.8c-.5.2-.8.6-.8 1.1v.6" />
      <line x1="8" y1="11.2" x2="8" y2="11.8" />
    </>
  ),
  // A bulb: an idea for the page.
  suggest: (
    <>
      <path d="M5.5 10.5a4 4 0 1 1 5 0v1.5h-5Z" />
      <line x1="6" y1="14" x2="10" y2="14" />
    </>
  ),
  close: (
    <>
      <line x1="3.5" y1="3.5" x2="12.5" y2="12.5" />
      <line x1="12.5" y1="3.5" x2="3.5" y2="12.5" />
    </>
  ),
  copy: (
    <>
      <rect x="5.5" y="5.5" width="8" height="8" />
      <polyline points="10.5,5.5 10.5,2.5 2.5,2.5 2.5,10.5 5.5,10.5" />
    </>
  ),
  open: (
    <>
      <polyline points="9,2.5 13.5,2.5 13.5,7" />
      <line x1="13.5" y1="2.5" x2="7" y2="9" />
      <path d="M11 10.5v3H2.5v-8.5h3" />
    </>
  ),
  edit: (
    <>
      <path d="M2.5 13.5v-3L10.5 2.5l3 3-8 8Z" />
      <line x1="9" y1="4" x2="12" y2="7" />
    </>
  ),
  back: (
    <>
      <line x1="13" y1="8" x2="3" y2="8" />
      <polyline points="7,4 3,8 7,12" />
    </>
  ),
  forward: (
    <>
      <line x1="3" y1="8" x2="13" y2="8" />
      <polyline points="9,4 13,8 9,12" />
    </>
  ),
  trash: (
    <>
      <line x1="2.5" y1="4.5" x2="13.5" y2="4.5" />
      <path d="M4 4.5l.7 9h6.6l.7-9" />
      <path d="M6 4.5V2.5h4v2" />
    </>
  ),
  pause: (
    <>
      <line x1="5.5" y1="3" x2="5.5" y2="13" />
      <line x1="10.5" y1="3" x2="10.5" y2="13" />
    </>
  ),
  play: <polygon points="4.5,3 12.5,8 4.5,13" />,
  pin: (
    <>
      <path d="M6 2.5h4l-.5 4 2 2.5H4.5l2-2.5Z" />
      <line x1="8" y1="9" x2="8" y2="14" />
    </>
  ),
  hide: (
    <>
      <path d="M1.5 8S4 3.5 8 3.5 14.5 8 14.5 8 12 12.5 8 12.5 1.5 8 1.5 8Z" />
      <circle cx="8" cy="8" r="1.8" />
      <line x1="3" y1="13" x2="13" y2="3" />
    </>
  ),
  checkAll: (
    <>
      <polyline points="1.5,8.5 4.5,11.5 9.5,5" />
      <polyline points="8.5,11.5 14.5,4.5" />
    </>
  ),
  up: (
    <>
      <line x1="8" y1="13" x2="8" y2="3" />
      <polyline points="4,7 8,3 12,7" />
    </>
  ),
  down: (
    <>
      <line x1="8" y1="3" x2="8" y2="13" />
      <polyline points="4,9 8,13 12,9" />
    </>
  ),
  undo: (
    <>
      <polyline points="5,3.5 2.5,6 5,8.5" />
      <path d="M2.5 6H10a3.5 3.5 0 0 1 0 7H6" />
    </>
  ),
  menu: (
    <>
      <line x1="2.5" y1="4" x2="13.5" y2="4" />
      <line x1="2.5" y1="8" x2="13.5" y2="8" />
      <line x1="2.5" y1="12" x2="13.5" y2="12" />
    </>
  ),
  clock: (
    <>
      <circle cx="8" cy="8" r="5.5" />
      <polyline points="8,4.5 8,8 10.5,9.5" />
    </>
  ),
  swap: (
    <>
      <line x1="2.5" y1="5" x2="13" y2="5" />
      <polyline points="10,2 13,5 10,8" />
      <line x1="13.5" y1="11" x2="3" y2="11" />
      <polyline points="6,8 3,11 6,14" />
    </>
  ),
  collapse: (
    <>
      <polyline points="8,3.5 3.5,8 8,12.5" />
      <polyline points="12.5,3.5 8,8 12.5,12.5" />
    </>
  ),
  chevron: <polyline points="4,6 8,10 12,6" />,
};

export function Icon({ glyph, size = 16 }: { glyph: Glyph; size?: number }) {
  return (
    <svg
      aria-hidden="true"
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="square"
      className="shrink-0"
    >
      {PATHS[glyph]}
    </svg>
  );
}

/** The one filled mark: something a model made (the prototype's "Fill for me"). */
export function Sparkle({ size = 13 }: { size?: number }) {
  return (
    <svg aria-hidden="true" width={size} height={size} viewBox="0 0 16 16" fill="currentColor" className="shrink-0">
      <path d="M8 0l1.6 5.2L15 6.5l-5.4 1.3L8 13 6.4 7.8 1 6.5l5.4-1.3Z" />
      <path d="M13 10l.7 2.3 2.3.7-2.3.7L13 16l-.7-2.3-2.3-.7 2.3-.7Z" opacity="0.6" />
    </svg>
  );
}
